import { useEffect, useState } from "react";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { useQuery } from "@tanstack/react-query";
import { gxDashboardAccountsBalanceApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { t } from "i18next";

export interface AccountBalanceItem {
  id: string;
  accountName: string;
  accountNumber: string;
  balance: number;
  currency: string;
  percentage: number;
  accountType: string;
  color: string;
}

interface BalanceByAccountProps {
  title?: string;
}

const ACCOUNT_TYPE_COLORS: Record<string, string> = {
  Operating: "#104484",
  Liability: "#FA8960",
  Reserve: "#3674C0",
};

const DEFAULT_ACCOUNT_TYPE_COLOR = "#CFCFCF";

export const getAccountTypeColor = (accountType?: string | null): string => {
  if (!accountType) return DEFAULT_ACCOUNT_TYPE_COLOR;
  return ACCOUNT_TYPE_COLORS[accountType] ?? DEFAULT_ACCOUNT_TYPE_COLOR;
};

const mapToAccountBalanceItems = (data: any): AccountBalanceItem[] => {
  if (!data) return [];

  const accounts = Array.isArray(data)
    ? data
    : (data.accountList ?? data.accounts ?? data.data ?? []);

  const totalBalance = accounts.reduce(
    (sum: number, item: any) => sum + Math.abs(item.availableBalance ?? 0),
    0,
  );

  return accounts.map((item: any, index: number) => ({
    id: item.accountNo ?? `${index}`,
    accountName: item.accountName ?? item.accountHolderName ?? "",
    accountNumber: item.accountNo ? `**** ${item.accountNo.slice(-4)}` : "",
    balance: item.availableBalance ?? 0,
    currency: item.currencyCode ?? item.currency ?? "AED",
    percentage:
      totalBalance > 0
        ? (Math.abs(item.availableBalance ?? 0) / totalBalance) * 100
        : 0,
    accountType: item.accountType ?? "",
    color: getAccountTypeColor(item.accountType),
  }));
};

const LEGEND_ITEMS: { accountType: string; labelKey: string }[] = [
  { accountType: "Operating", labelKey: "operating" },
  { accountType: "Reserve", labelKey: "reserve" },
  { accountType: "Liability", labelKey: "liability" },
];

const AccountProgressBar = ({ account }: { account: AccountBalanceItem }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setWidth(account.percentage));
    return () => cancelAnimationFrame(frame);
  }, [account.percentage]);

  return (
    <div className="gx-boa-progress-track">
      <div
        className="gx-boa-progress-fill"
        style={{
          width: `${width}%`,
          backgroundColor: account.color,
        }}
      />
    </div>
  );
};

const renderAccountRow = (
  account: AccountBalanceItem,
  formatAmount: (item: AccountBalanceItem) => string,
) => {
  const isNegative = account.balance < 0;
  const currencySymbol = gxCurrencyFormat(0, account.currency).symbol;

  return (
    <div className="gx-boa-row" key={account.id}>
      <div className="gx-boa-row-header">
        <span className="gx-p14-m gx-boa-account-name">
          {account.accountName} {account.accountNumber}
        </span>

        <span
          className={`gx-p14-b gx-boa-amount ${isNegative ? "gx-negative" : ""}`}
        >
          {isNegative ? "- " : ""}
          {currencySymbol} {formatAmount(account)}
        </span>
      </div>
      <AccountProgressBar account={account} />
    </div>
  );
};

function BalanceByAccount({ title }: BalanceByAccountProps) {
  const {
    data: balanceData,
    isLoading,
    error: balanceApiError,
  } = useQuery({
    queryKey: ["dashboardAccountsBalance"],
    queryFn: async () => {
      const response = await gxDashboardAccountsBalanceApi({
        pageNo: 0,
        pageSize: 0,
      });

      if (response.status === 1) {
        return response.data;
      }

      throw new Error(response.errorMessage);
    },
  });

  useEffect(() => {
    if (balanceApiError) {
      console.error(
        "BalanceByAccount: gxDashboardAccountsBalanceApi failed",
        balanceApiError,
      );
    }
  }, [balanceApiError]);

  const accounts = mapToAccountBalanceItems(balanceData);

  const formatAmount = (account: AccountBalanceItem) => {
    const formatResult = gxCurrencyFormat(
      Math.abs(account.balance),
      account.currency,
    );
    return `${formatResult.integer}${formatResult.decimal ?? ""}`;
  };

  return (
    <div className="gx-card-box-alter gx-main-balance-account relative">
      {isLoading && (
        <div className="gx-bene-form-container-loading-overlay">
          <SubmitLoading />
        </div>
      )}

      <h1 className="gx-h4-b gx-Balance-by-Account">
        {title || t("balance_by_account")}
      </h1>

      {!isLoading && accounts.length === 0 ? (
        <NoDataFound message="No accounts found" />
      ) : (
        <>
          <div className="gx-boa-list">
            {accounts.map((account) => renderAccountRow(account, formatAmount))}
          </div>

          <div className="gx-boa-legend">
            {LEGEND_ITEMS.map((legendItem) => (
              <span className="gx-boa-legend-item" key={legendItem.accountType}>
                <span
                  className="gx-boa-legend-dot"
                  style={{
                    backgroundColor: getAccountTypeColor(
                      legendItem.accountType,
                    ),
                  }}
                />
                {t(legendItem.accountType)}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default BalanceByAccount;
